import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { getCrm, numberFormat, toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

async function ExportToExcel({
    data,
    branches,
    branchId,
    filter,
    filterDate,
    dataProcess,
    branchInfo,
    t,
}: {
    data: any
    branches: any[]
    branchId: number
    filter: any
    filterDate: string
    dataProcess: any
    branchInfo: any
    t: any
}) {
    try {
        if (!data) {
            alert(t('REPORT.TITLE.NO_DATA'));
            return;
        }
        const workbook = new ExcelJS.Workbook()
        const sheet = workbook.addWorksheet(t('REPORT.REPORT_NAME.REMITTANCE_NEW'))

        const titleRow = sheet.addRow([t('REPORT.REPORT_NAME.REMITTANCE_NEW')])
        titleRow.font = { bold: true, size: 16 }
        sheet.mergeCells(titleRow.number, 1, titleRow.number, 20)
        titleRow.alignment = { horizontal: 'left' }

        // Filter info (horizontal)
        const filterLabels = [
            t('REPORT.FILTER.BRANCH'),
            t('REPORT.FILTER.DATE'),
        ]
        const filterValues = [
            branchId === 0 ? t('REPORT.FILTER.ALL') : _.get(_.find(branches, { branchId }), 'branchName', ''),
            filterDate ? toBuddhistYear(moment(filterDate), 'DD/MM/YYYY') : '-',
        ]

        const filterRowData: any[] = []
        for (let i = 0; i < filterLabels.length; i++) {
            filterRowData.push(filterLabels[i] + ':', filterValues[i])
        }
        const filterRow = sheet.addRow(filterRowData)
        filterRow.font = { size: 11 }
        for (let col = 1; col <= filterRowData.length; col += 2) {
            filterRow.getCell(col).font = { bold: true }
            filterRow.getCell(col).alignment = { horizontal: 'left' }
            filterRow.getCell(col + 1).alignment = { horizontal: 'left' }
        }
        sheet.addRow([])

        const headCells = [
            { label: t('REPORT.TABLE.REMITTANCE.LIST'), width: 50 },
            { label: t('REPORT.TABLE.REMITTANCE.QTY'), width: 20 },
            { label: '', width: 20 },
        ]

        sheet.columns = headCells.map(c => ({ width: c.width }))

        const headerRow = sheet.addRow(headCells.map(c => c.label))
        headerRow.font = { bold: true, size: 12 }
        headerRow.alignment = { horizontal: 'left', vertical: 'middle' }

        const { DF, LAB, TREATMENTRIGHT, CASHSLIP, INCOME_EXPENSES, SUMMARY_TREATMENT_TYPE } = dataProcess
        const calculateAdvance = JSON.parse(branchInfo)?.calculateAdvance

        // Calculate totals
        const total = data.rowDf?.length > 0 || data.rowExpenses?.length > 0 || data.rowTreatmentRight?.length > 0 ? _.sumBy(data.rowDf, 'price') + _.sumBy(data.rowExpenses, 'price') : 0
        const expenses = INCOME_EXPENSES.isShow ? _.sumBy(data.rowExpensesCash, 'price') + _.sumBy(data.rowExpensesTransfer, 'price') + _.sumBy(data.rowExpensesDebit, 'price') + _.sumBy(data.rowExpensesCredit, 'price') : 0
        const df = _.sumBy(data.rowDf, 'price')
        const treatmentRight = TREATMENTRIGHT.isProcess ? _.sumBy(data.rowTreatmentRight, 'price') : 0

        const sumAdvance = calculateAdvance === "INCREASE" ? _.sumBy(data.rowAdvance, 'price') || 0 : _.sumBy(data.rowPaidAdvance, 'price') || 0

        const totalIncome = _.sumBy(data.rowIncomeCash?.filter((item: { expenseGroup: string }) => item.expenseGroup === "INCOME"), 'treatment') +
            _.sumBy(data.rowIncomeTransfer?.filter((item: { expenseGroup: string }) => item.expenseGroup === "INCOME"), 'treatment') +
            _.sumBy(data.rowIncomeDebit?.filter((item: { expenseGroup: string }) => item.expenseGroup === "INCOME"), 'treatment')

        const income = CASHSLIP.isProcess ? _.sumBy(data.rowIncomeCash, 'treatment') : _.sumBy(data.rowDailyBalance, 'sumTreatment') +
            _.sumBy(data.rowDailyBalance, 'sumProduct') + treatmentRight + sumAdvance - (INCOME_EXPENSES.isProcess ? 0 : totalIncome) - (_.sumBy(data.rowPaidAdvance, 'price'))

        // Daily Balance
        if (data.rowDailyBalance && data.rowDailyBalance.length > 0) {
            for (const val of data.rowDailyBalance) {
                sheet.addRow([
                    t('REPORT.TABLE.REMITTANCE.NET_DAILY'),
                    numberFormat(calculateAdvance === 'INCREASE' ? val.sumTreatment + val.sumProduct + treatmentRight + sumAdvance - (INCOME_EXPENSES.isProcess ? 0 : totalIncome) - (_.sumBy(data.rowPaidAdvance, 'price')) : val.sumTreatment + val.sumProduct + treatmentRight - (INCOME_EXPENSES.isProcess ? 0 : totalIncome)),
                    ''
                ])

                sheet.addRow([
                    `  ${t('REPORT.TABLE.REMITTANCE.NET_OPERATIVE')}`,
                    numberFormat(val.sumTreatment),
                    ''
                ])

                sheet.addRow([
                    `  ${t('REPORT.TABLE.REMITTANCE.NET_PRODUCT')}`,
                    numberFormat(val.sumProduct),
                    ''
                ])

                if (INCOME_EXPENSES.isShow) {
                    sheet.addRow([
                        `  ${t('REPORT.TABLE.REMITTANCE.ANOTHER')}`,
                        numberFormat(INCOME_EXPENSES.isProcess ? totalIncome : 0),
                        ''
                    ])
                }
            }
        }

        // Cash
        const cashTotal = INCOME_EXPENSES.isProcess ? _.sumBy(data.rowIncomeCash, 'treatment') || 0 : _.sumBy(data.rowIncomeCash?.filter((item: { expenseGroup: string }) => item.expenseGroup !== "INCOME"), 'treatment') || 0
        sheet.addRow([
            `  ${t('REPORT.TABLE.REMITTANCE.CASH')}`,
            numberFormat(cashTotal),
            ''
        ])

        for (const val of data.rowIncomeCash || []) {
            if (!INCOME_EXPENSES.isShow && val.expenseGroup === "INCOME") continue
            sheet.addRow([
                `    - ${val.detail || t('REPORT.TITLE.TREATMENT_PRODUCT')}`,
                numberFormat(val.treatment),
                ''
            ])
        }

        // Transfer
        const transferTotal = _.sumBy(data.rowIncomeTransfer, 'treatment') || 0
        sheet.addRow([
            `  ${t('REPORT.TABLE.REMITTANCE.TRANSFER_')}`,
            numberFormat(transferTotal),
            ''
        ])

        for (const val of data.rowIncomeTransfer || []) {
            if (!INCOME_EXPENSES.isShow && val.expenseGroup === "INCOME") continue
            if (val.treatment > 0) {
                sheet.addRow([
                    `    - ${val.expenseGroup === "INCOME" ? val.detail : `${t('REPORT.TABLE.REMUNERATION.PRODUCT_TREATMENT')} (${val.detail})`}`,
                    numberFormat(val.treatment || 0),
                    ''
                ])
            }
        }

        // Voucher (if CRM enabled)
        if (getCrm()) {
            const voucherTotal = _.sumBy(data.rowIncomeVoucher, 'treatment') || 0
            sheet.addRow([
                `  ${t('REPORT.PAYMENT_CHANNEL.7')}`,
                numberFormat(voucherTotal),
                ''
            ])

            for (const val of data.rowIncomeVoucher || []) {
                if (!INCOME_EXPENSES.isShow && val.expenseGroup === "INCOME") continue
                if (val.treatment > 0) {
                    sheet.addRow([
                        `    - ${val.expenseGroup === "INCOME" ? val.detail : `${t('REPORT.TABLE.REMUNERATION.PRODUCT_TREATMENT')} (${val.detail})`}`,
                        numberFormat(val.treatment || 0),
                        ''
                    ])
                }
            }

            const promotionTotal = _.sumBy(data.rowIncomePromotion, 'treatment') || 0
            sheet.addRow([
                `  ${t('REPORT.PAYMENT_CHANNEL.8')}`,
                numberFormat(promotionTotal),
                ''
            ])

            for (const val of data.rowIncomePromotion || []) {
                if (!INCOME_EXPENSES.isShow && val.expenseGroup === "INCOME") continue
                if (val.treatment > 0) {
                    sheet.addRow([
                        `    - ${val.expenseGroup === "INCOME" ? val.detail : `${t('REPORT.TABLE.REMUNERATION.PRODUCT_TREATMENT')} (${val.detail})`}`,
                        numberFormat(val.treatment || 0),
                        ''
                    ])
                }
            }
        }

        // Check
        const checkTotal = _.sumBy(data.rowIncomeCheck, 'treatment') || 0
        sheet.addRow([
            `  ${t('REPORT.PAYMENT_CHANNEL.9')}`,
            numberFormat(checkTotal),
            ''
        ])

        for (const val of data.rowIncomeCheck || []) {
            if (!INCOME_EXPENSES.isShow && val.expenseGroup === "INCOME") continue
            if (val.treatment > 0) {
                sheet.addRow([
                    `    - ${val.expenseGroup === "INCOME" ? val.detail : `${t('REPORT.TABLE.REMUNERATION.PRODUCT_TREATMENT')} (${val.detail})`}`,
                    numberFormat(val.treatment || 0),
                    ''
                ])
            }
        }

        // Debit
        const debitTotal = _.sumBy(data.rowIncomeDebit, 'treatment')
        sheet.addRow([
            `  ${t('REPORT.TABLE.REMITTANCE.DEBIT')}`,
            numberFormat(debitTotal),
            ''
        ])

        for (const val of data.rowIncomeDebit || []) {
            if (!INCOME_EXPENSES.isShow && val.expenseGroup === "INCOME") continue
            if (val.treatment > 0) {
                sheet.addRow([
                    `    - ${val.expenseGroup === "INCOME" ? val.detail : `${t('REPORT.TABLE.REMUNERATION.PRODUCT_TREATMENT')} (${val.detail})`}`,
                    numberFormat(val.treatment),
                    ''
                ])
            }
        }

        // Credit
        const creditTotal = _.sumBy(data.rowIncomeCredit, 'treatment') || 0
        sheet.addRow([
            `  ${t('REPORT.TABLE.REMITTANCE.CREDIT')}`,
            numberFormat(creditTotal),
            ''
        ])

        for (const val of data.rowIncomeCredit || []) {
            if (!INCOME_EXPENSES.isShow && val.expenseGroup === "INCOME") continue
            if (val.treatment > 0) {
                sheet.addRow([
                    `    - ${val.expenseGroup === "INCOME" ? val.detail : `${t('REPORT.TABLE.REMUNERATION.PRODUCT_TREATMENT')} (${val.detail})`}`,
                    numberFormat(val.treatment || 0),
                    ''
                ])
            }
        }

        // Treatment Right
        if (TREATMENTRIGHT.isShow) {
            const treatmentRightTotal = _.sumBy(data.rowTreatmentRight, 'price') || 0
            sheet.addRow([
                `  ${t('REPORT.TABLE.REMITTANCE.TREATMENT_RIGHT')}`,
                numberFormat(treatmentRightTotal),
                ''
            ])

            for (const val of data.rowTreatmentRight || []) {
                sheet.addRow([
                    `    - ${val.treatmentRightName}`,
                    numberFormat(val.price || 0),
                    ''
                ])
            }
        }

        // Advance Paid
        const paidAdvanceTotal = _.sumBy(data.rowPaidAdvance, 'price') || 0
        sheet.addRow([
            `  ${t('REPORT.TABLE.REMITTANCE.ADVANCE_PAID')}${calculateAdvance === 'INCREASE' ? t('REPORT.TABLE.REMITTANCE.NOT_PROGRESS') : ''}`,
            numberFormat(paidAdvanceTotal),
            ''
        ])

        // Advance Not Progress
        const advanceTotal = _.sumBy(data.rowAdvance, 'price') || 0
        sheet.addRow([
            `  ${t('REPORT.TABLE.REMITTANCE.ADVANCE_NOT_PROGRESS')}${calculateAdvance === 'DECREASE' ? t('REPORT.TABLE.REMITTANCE.NOT_PROGRESS') : ''}`,
            numberFormat(advanceTotal),
            ''
        ])

        // Expenses
        if (INCOME_EXPENSES.isShow) {
            const totalExpenses = _.sumBy(data.rowExpensesCash, 'price') + _.sumBy(data.rowExpensesTransfer, 'price') + _.sumBy(data.rowExpensesDebit, 'price') + _.sumBy(data.rowExpensesCredit, 'price')
            sheet.addRow([
                t('REPORT.TABLE.REMITTANCE.TOTAL_EXPENSES'),
                numberFormat(totalExpenses),
                ''
            ])

            // Expenses Cash
            const expensesCashTotal = _.sumBy(data.rowExpensesCash, 'price') || 0
            sheet.addRow([
                `  ${t('REPORT.TABLE.REMITTANCE.CASH')}`,
                numberFormat(expensesCashTotal),
                ''
            ])

            for (const val of data.rowExpensesCash || []) {
                sheet.addRow([
                    `    - ${val.expenseTypeName}`,
                    numberFormat(val.price),
                    ''
                ])
            }

            // Expenses Transfer
            const expensesTransferTotal = _.sumBy(data.rowExpensesTransfer, 'price') || 0
            sheet.addRow([
                `  ${t('REPORT.TABLE.REMITTANCE.TRANSFER_')}`,
                numberFormat(expensesTransferTotal),
                ''
            ])

            for (const val of data.rowExpensesTransfer || []) {
                if (val.price > 0) {
                    sheet.addRow([
                        `    - ${val.expenseTypeName}`,
                        numberFormat(val.price || 0),
                        ''
                    ])
                }
            }

            // Expenses Debit
            const expensesDebitTotal = _.sumBy(data.rowExpensesDebit, 'price')
            sheet.addRow([
                `  ${t('REPORT.TABLE.REMITTANCE.DEBIT')}`,
                numberFormat(expensesDebitTotal),
                ''
            ])

            for (const val of data.rowExpensesDebit || []) {
                if (val.price > 0) {
                    sheet.addRow([
                        `    - ${val.expenseTypeName}`,
                        numberFormat(val.price),
                        ''
                    ])
                }
            }

            // Expenses Credit
            const expensesCreditTotal = _.sumBy(data.rowExpensesCredit, 'price') || 0
            sheet.addRow([
                `  ${t('REPORT.TABLE.REMITTANCE.CREDIT')}`,
                numberFormat(expensesCreditTotal),
                ''
            ])

            for (const val of data.rowExpensesCredit || []) {
                if (val.price > 0) {
                    sheet.addRow([
                        `    - ${val.expenseTypeName}`,
                        numberFormat(val.price || 0),
                        ''
                    ])
                }
            }

            // Expenses Check
            const expensesCheckTotal = _.sumBy(data.rowExpensesCheck, 'price') || 0
            sheet.addRow([
                `  ${t('REPORT.PAYMENT_CHANNEL.9')}`,
                numberFormat(expensesCheckTotal),
                ''
            ])

            for (const val of data.rowExpensesCheck || []) {
                if (val.price > 0) {
                    sheet.addRow([
                        `    - ${val.expenseTypeName}`,
                        numberFormat(val.price || 0),
                        ''
                    ])
                }
            }
        }

        // DF
        if (DF?.isShow) {
            const dfTotal = _.sumBy(data.rowDf, 'price') || 0
            sheet.addRow([
                t('REPORT.TABLE.REMITTANCE.SUM_FEE_DOCTOR'),
                numberFormat(dfTotal),
                ''
            ])

            for (const val of data.rowDf || []) {
                if (val.doctorId) {
                    sheet.addRow([
                        `  - ${val.detail}`,
                        numberFormat(val.price || 0),
                        `(${t('REPORT.TITLE.TREATMENT')} ${numberFormat(val.total || 0)})`
                    ])
                }
            }
        }

        // Summary rows
        sheet.addRow(['', '', ''])
        sheet.addRow([
            t('REPORT.TABLE.REMITTANCE.TOTAL_INCOME'),
            numberFormat(income),
            ''
        ])
        sheet.addRow([
            t('REPORT.TABLE.REMITTANCE.TOTAL_INCOME_EXPENSES'),
            numberFormat((INCOME_EXPENSES.isProcess ? expenses : 0) + ((DF.isProcess ? df : 0))),
            ''
        ])
        sheet.addRow([
            t('REPORT.TABLE.REMITTANCE.TOTAL'),
            numberFormat(income - ((INCOME_EXPENSES.isProcess ? expenses : 0) + (DF.isProcess ? df : 0))),
            ''
        ])

        // Treatment Type Summary
        if (SUMMARY_TREATMENT_TYPE?.isShow) {
            const treatmentTypeTotal = _.sumBy(data.rowTreatmentType, 'total') || 0
            sheet.addRow([
                t('REPORT.TABLE.REMITTANCE.TREATMENT_TYPE'),
                numberFormat(treatmentTypeTotal),
                ''
            ])

            for (const val of data.rowTreatmentType || []) {
                sheet.addRow([
                    `  ${val.operativeTypeName}`,
                    numberFormat(val.total),
                    ''
                ])
            }
        }

        forceTextOnDateCells(workbook)
        const buffer = await workbook.xlsx.writeBuffer()
        const timestamp = moment().format('YYYYMMDD_HHmmss')
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, `${t('REPORT.REPORT_NAME.REMITTANCE_NEW')}_${timestamp}.xlsx`)
    } catch (error) {
        console.error('Error exporting to Excel:', error);
    }
}

export default ExportToExcel 